/**
 * Redacción de PII en texto libre que viaja a Sentry.
 *
 * ⚠️ NO LO BORRES — la instrucción anterior de este encabezado era INSEGURA
 * ---------------------------------------------------------------------------
 * Hasta el rebase del 2026-09-16 (el que resolvió el conflicto con el #230 ya
 * mergeado) aquí decía «MÓDULO TEMPORAL — BÓRRALO AL REBASEAR SOBRE EL PR
 * #230», porque se daba por hecho que `piiRedaction.redactText()` hacía «esto
 * MISMO y mejor». Se volvió a MEDIR ejecutando los dos redactores sobre la
 * misma batería y NO es cierto: `redactText` deja pasar entero, en texto libre,
 * exactamente lo que este PR empieza a mandar a Sentry (el mensaje del backend):
 *
 *   - `Authorization: Bearer eyJhbGciOiJIUzI1NiJ9.…`  → sale ENTERO.
 *     Aquí lo corta `BEARER_RE`.
 *   - `password: hunter2` / `{"api_key":"sk_live_…"}` / `token=ab12…` cuando NO
 *     van en un query string → salen ENTEROS. El `SECRET_PARAM_RE` del #230
 *     exige el prefijo `[?&#]` y el `=`, así que solo cubre URLs.
 *     Aquí los corta `SECRET_KV_RE`.
 *   - `5551234567x89` (teléfono con extensión) → sale ENTERO, porque el
 *     `(?![A-Za-z0-9])` del `PHONE_CANDIDATE_RE` falla contra la `x` y ninguna
 *     vuelta atrás encuentra frontera. Aquí lo corta `BARE_PHONE_RE`.
 *
 * Por eso los DOS redactores conviven y se aplican en cadena: éste primero
 * sobre el texto libre no confiable (`safeText` en `apiError.js` y
 * `redactExceptionValues` en `init.js`), y el del #230 después como barrido de
 * todo el evento. Sus marcadores no vuelven a casar con los patrones del otro,
 * así que encadenarlos no degrada el texto. La unificación es deuda real, ya
 * registrada en el issue #313 — pero es un MERGE de patrones, no un borrado.
 *
 * En qué el #230 SÍ es mejor, MEDIDO ejecutando los dos módulos (la revisión de
 * @arturogaro corrigió lo que este encabezado afirmaba antes, que era falso en
 * dos de tres puntos — ver `decisions.md` D13):
 *
 *   - **barrido recursivo** de `contexts` / `breadcrumbs` / `extra` / `tags`
 *     (`deepScrub`). Aquí NO existe: `init.js` solo limpia claves de primer
 *     nivel. Ésta es la diferencia grande y es cierta.
 *   - **fronteras sin lookbehind** por diseño explícito (aquí también se evita,
 *     por el mismo motivo: Safari < 16.4).
 *
 * Y en qué NO es mejor, contra lo que decía este encabezado:
 *
 *   - *"sin JIDs de WhatsApp"* → **falso**. `redactPii('5215544332211@c.us')`
 *     devuelve `'[email]'`: la `EMAIL_RE` de aquí es idéntica a la del #230 y
 *     `c.us` cumple `[A-Za-z]{2,}`. También cae `@s.whatsapp.net`.
 *   - *"umbral por longitud en vez de por dígitos reales"* → **falso**.
 *     `SEPARATED_PHONE_RE` cuenta dígitos reales en el replacer, el mismo
 *     mecanismo que el #230.
 *
 * La diferencia real de cobertura ERA el rango de dígitos (aquí 10–15, allá ≥7
 * sin tope), y por eso se le escapaba un teléfono local mexicano de 8 dígitos.
 * Se cerró: este módulo ya usa el mismo umbral ≥7 sin tope. Ver D8.
 *
 * El rebase sobre el #230 — HECHO el 2026-09-16
 * ---------------------------------------------
 * Los pasos 1 y 2 del plan anterior (borrar este archivo; cambiar `safeText()`
 * para que use `redactText`) quedaron DESCARTADOS por lo medido arriba: los dos
 * habrían reabierto las tres fugas. `safeText()` sigue usando `redactPii`.
 *
 * El paso 3 sigue vigente y SE CUMPLE: el truncado a 500 caracteres vive en
 * `apiError.js` (`safeText`) y NO aquí, así que ningún cambio de redactor puede
 * tirarlo en silencio. Un backend que devuelve una página de error de 40 KB
 * sigue llegando cortada.
 *
 * El conflicto real estuvo solo en `init.js` y se resolvió a mano conservando
 * los DOS lados: el barrido recursivo del #230 y el dedup de la copia del
 * handler global de este PR. Ver `decisions.md`.
 *
 * ⚠️ **SÍ hay conflicto de archivos con el #230**, contra lo que decía el
 * cuerpo del PR: `git merge-tree` da CONFLICT en
 * `apps/host/src/observability/init.js` — las dos ramas reescriben `scrubPii`.
 * El rebase es manual en ese archivo. Verificado el 2026-09-09 y
 * reconfirmado el 2026-09-14 con las dos ramas ya rebasadas sobre `master`
 * (las dos a 0 detrás): el conflicto sigue ahí, contra
 * `origin/fix/dashboard/sentry-redux-state-pii` (0cadcb4e).
 *
 * ⚠️⚠️ Y OJO CON CÓMO PARTE ESE CONFLICTO: el segundo hunk corta a media
 * línea un bloque de comentario. El `/**` que lo abre queda ARRIBA del
 * marcador `<<<<<<<`, o sea que pertenece a los dos lados a la vez, y cada
 * lado lo continúa con contenido distinto. Quitar los marcadores y pegar los
 * dos lados en bruto ROMPE LA SINTAXIS: queda un solo `/**` abriendo, dos
 * cuerpos de comentario encadenados, y el código de esta rama atrapado dentro
 * del comentario. La anatomía completa y la resolución correcta están en
 * `docs/specs/2026-09-02-api-error-preserva-causa/rollout.md`.
 *
 * Por qué existe este archivo
 * ---------------------------
 * Al preservar la causa original de los errores de API (ver `apiError.js`),
 * los mensajes que produce el backend empiezan a viajar a Sentry — cosa que
 * antes NO pasaba, porque el patrón `catch { throw new Error("CODIGO") }`
 * tiraba la excepción original y solo llegaba el código opaco.
 *
 * Ese texto del backend NO es confiable: ya hay precedente documentado en
 * este proyecto de un mensaje de Prisma que traía el TELÉFONO del cliente
 * embebido (`MongoObjectId: <teléfono>`), y acabó indexado en el título de
 * un issue de Sentry, viajando además a alertas y correos. Ese incidente
 * costó trabajo real de limpieza.
 *
 * Como la preservación de la causa es justamente lo que vuelve a abrir esa
 * superficie, la redacción va junto con ella — no después.
 *
 * Trade-off aceptado explícitamente
 * ---------------------------------
 * Con el umbral en 7 dígitos, el patrón también atrapa timestamps, duraciones
 * en milisegundos y cualquier identificador numérico largo dentro del texto de
 * un error. Se pierde algo de señal diagnóstica a cambio de no dejar pasar un
 * teléfono.
 *
 * Se eligió así a propósito, y además es el umbral que ya usa el #230, que es
 * el redactor que se queda. Perder un timestamp crudo dentro del texto de un
 * error es barato; volver a indexar el teléfono de un cliente en Sentry ya se
 * demostró caro. Dos excepciones explícitas, con test:
 *   - los ObjectId de Mongo (24 hex) NO se tocan — los `\b` impiden hacer
 *     match parcial dentro de un token más largo;
 *   - las FECHAS en formato ISO o `dd/mm/aaaa` NO se tocan, aunque tengan 8
 *     dígitos: son señal diagnóstica pura y no hay teléfono con esa forma.
 */

// Correos completos. Va primero para que el dominio no quede huérfano tras
// redactar la parte numérica de un local-part tipo `5215512345678@dominio`.
// Cubre también los JIDs de WhatsApp (`…@c.us`, `…@s.whatsapp.net`).
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

// Credenciales en texto libre. El dashboard recibe magic links y callbacks de
// OAuth, y un backend puede devolver el header o la query que falló dentro del
// mensaje de error. `init.js` limpia `request.query_string` y las claves de
// primer nivel de `extra`, pero NO el texto de la excepción — que es justo lo
// que este PR empieza a mandar a Sentry.
const BEARER_RE = /\b(Bearer|Basic|Token)\s+[A-Za-z0-9._~+/=-]{8,}/gi;
const SECRET_KV_RE =
  /\b((?:access_|refresh_|page)?token|api[_-]?key|apikey|password|secret|authorization)(["']?\s*[:=]\s*["']?)([^\s"'&,;)}\]]{4,})/gi;

// Teléfonos en formato internacional con separadores: `+52 221 206 4500`,
// `+52-221-206-4500`, `+522212064500`.
const INTL_PHONE_RE = /\+\d[\d\s().-]{8,17}\d/g;

// Corridas "desnudas" de 7 dígitos o más. Los `\b` evitan tocar tokens más
// largos (p.ej. un ObjectId de 24 caracteres o un hash numérico).
//
// La extensión opcional al final (`x89`, `ext. 89`) NO es un adorno: sin ella
// el `\b` final no casaba y `'5551234567x89'` viajaba ENTERO a Sentry. Lo cazó
// la revisión de @diegosiac.
const BARE_PHONE_RE = /\b\d{7,}(?:\s*(?:x|ext\.?)\s*\d{1,5})?\b/gi;

// Teléfonos con separadores y SIN prefijo internacional: `55-1234-5678`,
// `(55) 1234 5678`, `55 1234 5678`, `221.206.4500`, `555-123-4567x89`. Ninguno
// de los dos patrones de arriba los atrapaba —`INTL_PHONE_RE` exige el `+` y
// `BARE_PHONE_RE` se rompe en cada separador—, así que un teléfono en formato
// nacional viajaba entero a Sentry. Es exactamente la fuga que este módulo
// existe para cerrar.
//
// El patrón solo propone CANDIDATOS; quien decide es el conteo de dígitos
// reales en el replacer, igual que hace `piiRedaction.js` del PR #230. Hacerlo
// así es lo que deja fuera las versiones (`1.2.3`) sin tener que enumerarlas.
//
// Las fronteras evitan morder dentro de un token alfanumérico más largo, y
// deliberadamente NO se usa lookbehind: Safari < 16.4 no lo soporta y el
// dashboard se sirve a navegadores de asesores que no controlamos.
const SEPARATED_PHONE_RE =
  /(^|[^A-Za-z0-9])(\d[\d\s().-]{5,20}\d)((?:\s*(?:x|ext\.?)\s*\d{1,5})?)(?![A-Za-z0-9])/gi;

/**
 * Mínimo de dígitos reales para tratar un candidato como teléfono.
 *
 * Era 10, y con 10 se le escapaba un teléfono local mexicano sin lada, que
 * tiene 8 (`4433-2211` pasaba intacto). Ahora es 7, el mismo umbral que usa el
 * `piiRedaction.js` del #230 — el redactor que se queda en producción.
 */
const MIN_PHONE_DIGITS = 7;

// Fechas: 8 dígitos, pero no son PII y sí son señal. `2026-09-04`, `04/09/2026`.
const DATE_LIKE_RE = /^(?:\d{4}[-/.]\d{1,2}[-/.]\d{1,2}|\d{1,2}[-/.]\d{1,2}[-/.]\d{4})$/;

const countDigits = (s) => (s.match(/\d/g) || []).length;

/**
 * Redacta PII de una cadena. Devuelve el valor original si no es string.
 *
 * @param {unknown} value
 * @returns {unknown} el string redactado, o `value` intacto si no era string
 */
export function redactPii(value) {
  if (typeof value !== 'string' || value.length === 0) return value;
  return (
    value
      .replace(EMAIL_RE, '[email]')
      // Los secretos van ANTES que los teléfonos: si un token trae una corrida
      // de dígitos, queremos que se redacte el token completo y no solo el
      // trozo numérico, que dejaría el resto de la credencial a la vista.
      .replace(BEARER_RE, '$1 [token]')
      .replace(SECRET_KV_RE, '$1$2[token]')
      .replace(INTL_PHONE_RE, '[phone]')
      .replace(BARE_PHONE_RE, '[phone]')
      // Va al final: los patrones anteriores ya convirtieron sus aciertos en
      // `[phone]`, así que aquí solo quedan los formatos con separador que se
      // les escapaban. El conteo se valida en el replacer y no en la regex,
      // porque `1 . . . . 2` casa con la forma pero no es un teléfono.
      // El cuarto grupo (la extensión) no se usa en el reemplazo a propósito:
      // se consume con el match y desaparece junto con el número.
      .replace(SEPARATED_PHONE_RE, (match, prefix, candidate) => {
        if (DATE_LIKE_RE.test(candidate)) return match;
        return countDigits(candidate) >= MIN_PHONE_DIGITS ? `${prefix}[phone]` : match;
      })
  );
}

export default redactPii;
